import React, { useEffect, useMemo, useState } from "react";
import { IdCard, Plus, Pencil, Trash2, FileDown, Search, X } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { PageHeader, SectionCard, Footer } from "@/components/common";
import ExportDialog from "@/components/ExportDialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { exportToPDF } from "@/lib/pdfExport";
import { expiryStatusClass, formatDateID } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUS_HEX = { Active: "#16a34a", "Hampir Expired": "#f59e0b", Expired: "#dc2626" };

function ParticipantDialog({ open, onOpenChange, initial, types, onSaved }) {
  const [name, setName] = useState("");
  const [bn, setBn] = useState("");
  const [pendorse, setPendorse] = useState("");
  const [simpers, setSimpers] = useState({});
  useEffect(() => {
    setName(initial?.name || ""); setBn(initial?.bn || ""); setPendorse(initial?.pendorse || "");
    const t = {};
    if (initial?.simpers) Object.entries(initial.simpers).forEach(([k, v]) => { t[k] = v ? v.slice(0, 10) : ""; });
    setSimpers(t);
  }, [initial, open]);

  const save = async () => {
    if (!name) { toast.error("Nama wajib diisi"); return; }
    const payload = { name, bn, pendorse, simpers: {} };
    Object.entries(simpers).forEach(([k, v]) => { if (v) payload.simpers[k] = v + "T00:00:00"; });
    try {
      if (initial?.id) await api.put(`/simper/participants/${initial.id}`, payload);
      else await api.post("/simper/participants", payload);
      toast.success("Data tersimpan"); onSaved(); onOpenChange(false);
    } catch (e) { toast.error("Gagal menyimpan"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ms-participant-form">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit" : "Tambah"} Peserta Simper</DialogTitle>
          <DialogDescription>Isi data peserta dan tanggal expired tiap simper. Aging &amp; status dihitung otomatis.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-3 gap-3 py-2">
          <div><Label className="text-xs">Nama</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="ms-name" /></div>
          <div><Label className="text-xs">BN</Label><Input value={bn} onChange={(e) => setBn(e.target.value)} data-testid="ms-bn" /></div>
          <div><Label className="text-xs">Position Title</Label><Input value={pendorse} onChange={(e) => setPendorse(e.target.value)} data-testid="ms-pendorse" /></div>
        </div>
        <div className="border-t pt-2">
          <p className="text-xs font-semibold text-slate-600 mb-2">Expired Date per Simper</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 max-h-72 overflow-y-auto pr-1">
            {types.map((t) => (
              <div key={t.id} className="flex items-center gap-2">
                <span className="text-[11px] text-slate-600 flex-1 truncate" title={t.name}>{t.name}</span>
                <Input type="date" className="h-8 w-36 text-xs" value={simpers[t.id] || ""} onChange={(e) => setSimpers((p) => ({ ...p, [t.id]: e.target.value }))} />
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="ms-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MonitoringSimper() {
  const { canEdit } = useAuth();
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [typeOpen, setTypeOpen] = useState(false);
  const [typeName, setTypeName] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [delTypeTarget, setDelTypeTarget] = useState(null);

  const load = async () => { const r = await api.get("/simper/matrix"); setData(r.data); };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.participants.filter((p) =>
      (statusFilter === "all" || p.overall_status === statusFilter) &&
      (!search || p.name?.toLowerCase().includes(search.toLowerCase()))
    );
  }, [data, statusFilter, search]);

  if (!data) return <div className="p-6"><div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>;

  const sc = data.summary.status_counts;
  const donut = [
    { name: "Active", value: sc.Active }, { name: "Hampir Expired", value: sc["Hampir Expired"] }, { name: "Expired", value: sc.Expired },
  ];
  const total = data.summary.total;
  const statusTotal = donut.reduce((a, d) => a + d.value, 0);

  const addType = async () => {
    if (!typeName.trim()) { toast.error("Nama simper wajib"); return; }
    try { await api.post("/simper/types", { name: typeName }); setTypeName(""); setTypeOpen(false); toast.success("Simper ditambahkan"); load(); }
    catch (e) { toast.error("Gagal menambah simper"); }
  };
  const delType = async (id) => { try { await api.delete(`/simper/types/${id}`); toast.success("Simper dihapus"); load(); } catch (e) { toast.error("Gagal"); } };
  const doDel = async () => { const p = delTarget; setDelTarget(null); try { await api.delete(`/simper/participants/${p.id}`); toast.success("Peserta dihapus"); load(); } catch (e) { toast.error("Gagal"); } };

  return (
    <div className="p-6" id="simper-page">
      <PageHeader icon={IdCard} title="MONITORING SIMPER" subtitle="Pantau status dan masa berlaku seluruh Simper di PT. Ithikhara"
        right={<div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="h-9 w-40" data-testid="ms-filter-status"><SelectValue placeholder="Semua Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua Status</SelectItem><SelectItem value="Active">Active</SelectItem><SelectItem value="Hampir Expired">Hampir Expired</SelectItem><SelectItem value="Expired">Expired</SelectItem></SelectContent></Select>
          <Button size="sm" variant="outline" className="h-9" onClick={() => setExportOpen(true)} data-testid="ms-export"><FileDown className="w-4 h-4 mr-1" />Export PDF</Button>
        </div>} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <SectionCard title="Status Simper">
          <div className="flex items-center gap-4">
            <div className="w-36 h-36 relative">
              <ResponsiveContainer><PieChart><Pie data={donut} dataKey="value" innerRadius={45} outerRadius={68} paddingAngle={2}>{donut.map((e) => <Cell key={e.name} fill={STATUS_HEX[e.name]} />)}</Pie></PieChart></ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="text-3xl font-extrabold">{statusTotal}</span><span className="text-xs text-slate-400">Total</span></div>
            </div>
            <div className="space-y-2 text-sm flex-1">
              {donut.map((d) => <div key={d.name} className="flex items-center justify-between"><span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_HEX[d.name] }} />{d.name}</span><span className="font-bold">{d.value}</span></div>)}
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Expired Tren (3 Next Month)">
          <ResponsiveContainer width="100%" height={170}><AreaChart data={data.summary.tren}><defs><linearGradient id="sg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#dc2626" stopOpacity={0.3} /><stop offset="100%" stopColor="#dc2626" stopOpacity={0} /></linearGradient></defs><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} /><Tooltip /><Area type="monotone" dataKey="value" stroke="#dc2626" strokeWidth={2} fill="url(#sg)" dot={{ r: 3 }} /></AreaChart></ResponsiveContainer>
        </SectionCard>

        <SectionCard title="List Simper Expired (Segera)">
          <div className="overflow-y-auto max-h-[190px]">
            <table className="w-full text-xs" data-testid="ms-expired-list"><thead className="sticky top-0 bg-white"><tr className="text-left text-slate-400 border-b"><th className="py-1.5">No</th><th>Nama Simper</th><th className="text-center">Jumlah Man Power</th><th>Status</th></tr></thead>
              <tbody>{data.summary.expired_list.map((e, i) => (<tr key={i} className="border-b border-slate-100"><td className="py-2">{i + 1}</td><td className="pr-2">{e.name}</td><td className="text-center font-semibold">{e.man_power}</td><td><span className={`px-2 py-0.5 rounded text-[10px] ${expiryStatusClass(e.status)}`}>{e.status}</span></td></tr>))}
                {data.summary.expired_list.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-slate-400">Tidak ada simper expired</td></tr>}</tbody></table>
          </div>
        </SectionCard>
      </div>

      <SectionCard title="HSE Matrix Simper PT. Ithikhara" right={<div className="flex items-center gap-2">
        <div className="relative"><Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" /><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama peserta Simper" className="h-9 w-56 pl-8" data-testid="ms-search" /></div>
        {canEdit && <Button size="sm" variant="outline" className="h-9" onClick={() => setTypeOpen(true)} data-testid="ms-add-type"><Plus className="w-4 h-4 mr-1" />Simper</Button>}
        {canEdit && <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-9" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="ms-add-participant"><Plus className="w-4 h-4 mr-1" />Peserta</Button>}
      </div>}>
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="text-xs border-collapse" data-testid="ms-matrix">
            <thead>
              <tr className="bg-slate-50">
                <th rowSpan={2} className="border-r border-slate-200 px-2 py-2 text-center sticky left-0 z-20 bg-slate-50 w-12">No</th>
                <th rowSpan={2} className="border-r border-slate-200 px-2 py-2 text-left min-w-[130px] sticky left-12 z-20 bg-slate-50">Nama</th>
                <th rowSpan={2} className="border-r border-slate-200 px-2 py-2 text-left">BN</th>
                <th rowSpan={2} className="border-r border-slate-200 px-2 py-2 text-left min-w-[150px]">Position Title</th>
                {data.types.map((t) => (
                  <th key={t.id} colSpan={3} className="border-r border-slate-200 px-2 py-1.5 text-center min-w-[220px]">
                    <div className="flex items-center justify-center gap-1"><span className="font-semibold">{t.name}</span>
                      {canEdit && <button onClick={() => setDelTypeTarget(t)} className="text-red-400 hover:text-red-600" title="Hapus simper" data-testid={`ms-type-del-${t.id}`}><X className="w-3 h-3" /></button>}</div>
                  </th>
                ))}
                <th rowSpan={2} className="px-2 py-2 text-center">Aksi</th>
              </tr>
              <tr className="bg-slate-50 text-slate-500">
                {data.types.map((t) => (<React.Fragment key={t.id}><th className="border-r border-slate-100 px-2 py-1 font-medium">Expired</th><th className="border-r border-slate-100 px-2 py-1 font-medium">Aging</th><th className="border-r border-slate-200 px-2 py-1 font-medium">Status</th></React.Fragment>))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, idx) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="border-r border-slate-200 px-2 py-2 text-center sticky left-0 z-10 bg-white w-12">{idx + 1}</td>
                  <td className="border-r border-slate-200 px-2 py-2 font-medium whitespace-nowrap sticky left-12 z-10 bg-white">{p.name}</td>
                  <td className="border-r border-slate-200 px-2 py-2">{p.bn}</td>
                  <td className="border-r border-slate-200 px-2 py-2 whitespace-nowrap">{p.pendorse}</td>
                  {data.types.map((t) => {
                    const c = p.computed?.[t.id] || {};
                    return (<React.Fragment key={t.id}>
                      <td className="border-r border-slate-100 px-2 py-2 whitespace-nowrap text-center">{c.expired ? formatDateID(c.expired) : "-"}</td>
                      <td className={`border-r border-slate-100 px-2 py-2 text-center font-semibold ${c.aging < 0 ? "text-red-500" : "text-slate-600"}`}>{c.aging ?? "-"}</td>
                      <td className="border-r border-slate-200 px-2 py-2 text-center">{c.status ? <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${expiryStatusClass(c.status)}`}>{c.status}</span> : "-"}</td>
                    </React.Fragment>);
                  })}
                  <td className="px-2 py-2 text-center whitespace-nowrap">{canEdit ? (<div className="flex items-center justify-center gap-1">
                    <button onClick={() => { setEditing(p); setFormOpen(true); }} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" data-testid={`ms-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => setDelTarget(p)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`ms-del-${p.id}`}><Trash2 className="w-4 h-4" /></button></div>) : "-"}</td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={4 + data.types.length * 3 + 1} className="text-center py-8 text-slate-400">Tidak ada peserta</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-400 mt-2">Menampilkan {filtered.length} peserta · Aging days & status dihitung otomatis (Active &gt; 30 hari, Hampir Expired ≤ 30, Expired &lt; 0)</p>
      </SectionCard>

      <ParticipantDialog open={formOpen} onOpenChange={setFormOpen} initial={editing} types={data.types} onSaved={load} />

      <Dialog open={typeOpen} onOpenChange={setTypeOpen}>
        <DialogContent className="max-w-sm" data-testid="ms-type-form">
          <DialogHeader><DialogTitle>Tambah Jenis Simper</DialogTitle><DialogDescription>Kolom baru (Expired, Aging, Status) akan ditambahkan ke matrix.</DialogDescription></DialogHeader>
          <Input value={typeName} onChange={(e) => setTypeName(e.target.value)} placeholder="Nama simper baru" data-testid="ms-type-name" />
          <DialogFooter><Button variant="outline" onClick={() => setTypeOpen(false)}>Batal</Button><Button className="bg-blue-600 hover:bg-blue-700" onClick={addType} data-testid="ms-type-save">Tambah</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Hapus peserta?</AlertDialogTitle><AlertDialogDescription>Data peserta {delTarget?.name} akan dihapus permanen.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={doDel} data-testid="ms-del-confirm">Hapus</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!delTypeTarget} onOpenChange={(o) => !o && setDelTypeTarget(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Hapus jenis simper?</AlertDialogTitle><AlertDialogDescription>Kolom "{delTypeTarget?.name}" beserta data expired-nya pada semua peserta akan dihapus.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => { const t = delTypeTarget; setDelTypeTarget(null); delType(t.id); }} data-testid="ms-type-del-confirm">Hapus</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>

      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} onExport={(o) => exportToPDF("simper-page", "Monitoring_Simper", o)} />
      <Footer />
    </div>
  );
}
